import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Globe, Facebook, Instagram, Linkedin, Youtube, Twitter, MessageCircle, Star, ExternalLink } from "lucide-react";
import { ContactDialog } from "@/components/ContactDialog";
import { api } from "@/lib/api";

const ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube, x: Twitter, twitter: Twitter, whatsapp: MessageCircle };

let cache = null;
const listeners = new Set();
export const reloadFooter = async () => {
  cache = (await api.get("/site/footer")).data;
  listeners.forEach((fn) => fn(cache));
};

const useFooter = () => {
  const [data, setData] = useState(cache);
  useEffect(() => {
    listeners.add(setData);
    if (!cache) reloadFooter().catch(() => {});
    return () => { listeners.delete(setData); };
  }, []);
  return data;
};

const LinkList = ({ title, items, testId }) => items?.length > 0 && (
  <div data-testid={testId}>
    <h4 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40 mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((l) => (
        <li key={l.id}><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white/75 hover:text-white inline-flex items-center gap-1.5 transition-colors">{l.label}<ExternalLink className="w-3 h-3 opacity-50" /></a></li>
      ))}
    </ul>
  </div>
);

const Contact = ({ f }) => (
  <ul className="space-y-2 text-sm text-white/75 mt-4" data-testid="footer-contact-details">
    {f.email && <li><a href={`mailto:${f.email}`} className="inline-flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail className="w-4 h-4 text-[#E60000]" />{f.email}</a></li>}
    {f.phone && <li><a href={`tel:${f.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="w-4 h-4 text-[#E60000]" />{f.phone}</a></li>}
    {f.address && <li className="inline-flex items-start gap-2"><MapPin className="w-4 h-4 text-[#E60000] mt-0.5 shrink-0" />{f.address}</li>}
  </ul>
);

const Socials = ({ items }) => (
  <div className="flex flex-wrap gap-2" data-testid="footer-socials">
    {items.map((s) => {
      const Icon = ICONS[s.platform.toLowerCase()] || Globe;
      return (
        <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" title={s.platform} aria-label={s.platform} data-testid={`footer-social-${s.platform.toLowerCase()}`}
          className="w-9 h-9 grid place-items-center rounded-md bg-white/5 border border-white/10 text-white/80 hover:bg-[#E60000] hover:border-[#E60000] hover:text-white transition-colors">
          <Icon className="w-4 h-4" />
        </a>
      );
    })}
  </div>
);

export const SiteFooter = ({ banner = true }) => {
  const f = useFooter();
  return (
    <footer data-testid="site-footer">
      {banner && (
        <div className="bg-[#E60000] text-white py-4 flex items-center justify-center gap-2 font-heading font-extrabold tracking-[0.2em] text-sm" data-testid="footer-service-banner">
          <Star className="w-3 h-3 fill-white" />SERVICE TO HUMANITY<Star className="w-3 h-3 fill-white" />
        </div>
      )}
      {f && (
        <div className="bg-[#111] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3">
                <img src="/images/gamls-logo.png" alt="GAMLS" className="w-10 h-10 bg-white rounded-full p-0.5" />
                <div className="font-heading font-extrabold">GAMLS <span className="text-[#E60000]">EventHub</span></div>
              </div>
              {f.about && <p className="text-sm text-white/60 mt-4 leading-relaxed" data-testid="footer-about">{f.about}</p>}
              <Contact f={f} />
            </div>
            <LinkList title="Partner websites" items={f.partners} testId="footer-partners" />
            <LinkList title="Important links" items={f.links} testId="footer-links" />
            <div className="space-y-5">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">Connect with us</h4>
              {f.socials?.length > 0 && <Socials items={f.socials} />}
              {f.contact_form_enabled && <ContactDialog />}
            </div>
          </div>
          <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex flex-col md:flex-row gap-2 md:items-center justify-between text-xs text-white/45">
              <span data-testid="footer-copyright">© {new Date().getFullYear()} {f.copyright}</span>
              {f.notice && <span className="md:max-w-xl md:text-right" data-testid="footer-notice">{f.notice}</span>}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
